import { regular, solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react'
import { Alert, Button, Card, Col, Container, Form, ListGroup, Modal, OverlayTrigger, Row, Tooltip } from 'react-bootstrap'
import { useTranslation } from 'react-i18next';
// import { useNavigate } from 'react-router-dom';
import { Metrics } from '../../api/Metrics';
import AppLoader from '../../components/AppLoader';
import AppSearch from '../../components/AppSearch';
import IconButton from '../../components/IconButton';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useToasts } from '../../contexts/toasts';
import { Metric } from '../../models/Metric';
import { useNavigate } from 'react-router-dom';

export default function MetricsList() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [metrics, setMetrics] = useState<Metric[] | null>();

  const [metricToDelete, setMetricToDelete] = useState<Metric>();
  const [metricDetail, setMetricDetail] = useState<Metric>();

  const [metric, setMetric] = useState<Metric>(new Metric());
  const [editing, setEditing] = useState<boolean>(false);
  const [validated, setValidated] = useState<boolean>(false);

  const [searchText, setSearchText] = useState<string>("");
  const [alphaSort, setAlphaSort] = useState<number>(-1);

  const [showDetailModal, setShowDetailModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);

  const { showLoader, hideLoader } = useFullScreenLoader();
  const { showToast } = useToasts();



  useEffect(() => {
    initMetrics();
    // eslint-disable-next-line
  }, []);

  const handleDeleteModalClose = () => setShowDeleteModal(false);
  const openDeleteModal = (metric: Metric) => {
    setShowDeleteModal(true);
    setMetricToDelete(metric);
  }

  const handleDetailModalClose = () => {
    setShowDetailModal(false);
  }

  const openDetailModal = (metric: Metric) => {
    setShowDetailModal(true);
    setMetricDetail(metric);
  }

  const handleCreateModalClose = () => {
    setShowCreateModal(false);
    setMetric(new Metric());
    setEditing(false)
    setValidated(false);
  }
  const openCreateModal = (metric?: Metric) => {
    if (metric) {
      setEditing(true);
      setMetric(metric)
    }
    else {
      setMetric(new Metric())
    }
    setShowCreateModal(true);
  }

  const reset = () => {
    setMetrics(null);
    initMetrics();
  }

  const handleSearchChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const value = evt.target.value;
    setSearchText(value);
  }

  const deleteMetric = () => {
    showLoader!();

    Metrics.deleteMetric(metricToDelete!.id!)
      .then(
        () => {
          hideLoader!();
          showToast!('metric-success', 'Metric deleted successfully', 'Delete Success', 'success');
          handleDeleteModalClose();
          reset();
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          hideLoader!();
          showToast!('Metric-error', message, `Metrics Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const editMetric = (metric: Metric) => { openCreateModal(metric) };


  const initMetrics = () => {
    Metrics.getMetrics()
      .then(
        ({ data: metrics }) => setMetrics(metrics),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          hideLoader!();
          showToast!('metric-error', message, `Metrics Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }


  const activateSort = (type: 'alpha') => {
    const sort = {
      'alpha': () => setAlphaSort(alphaSort === 1 ? -1 : alphaSort + 1),
    }
    sort[type]();
  }

  function compareForSort(a: any, b: any) {
    if (a < b) {
      return -1
    }
    if (a > b) {
      return 1
    }
    return 0;
  }

  const handleFieldChange = (field: keyof Metric) => (evt: React.ChangeEvent<any>) => {
    const value = evt.target.value;
    setMetric({
      ...metric,
      [field]: value,
    })
  }

  const handleSubmit = (evt: React.FormEvent<HTMLFormElement>) => {
    const form = evt.currentTarget;
    evt.stopPropagation();
    evt.preventDefault();
    setValidated(true);

    if (form.checkValidity() === true) {

      showLoader!();
      const promise = editing
        ? Metrics.updateMetric(metric)
        : Metrics.createMetric(metric);

      promise
        .then(
          () => {
            hideLoader!();
            showToast!('metric-success', `Metric ${editing ? 'Updated' : 'Created'} successfully`, 'Success', 'success');
            reset();
            setMetric(new Metric());
            handleCreateModalClose();
          },
          (err) => {
            const message = err.response?.data?.message || err.message;
            const status = err.response?.status;
            hideLoader!();
            showToast!('metric-error', message, `Metrics Error ${status ? `(${status})` : ''}`, 'danger');
          }
        )
    }

  }

  const renderMetrics = () => {
    const filteredMetrics = metrics!.filter(metric => metric.name.toLowerCase().includes(searchText.toLowerCase())).sort((a, b) => {
      let result;
      alphaSort < 0 ? result = 0 : alphaSort > 0 ? result = compareForSort(a.name, b.name) : result = compareForSort(b.name, a.name)
      return result;
    })


    return (
      <>
        <Row className="gx-2 mb-3">
          <Col md={11}>
            <AppSearch value={searchText} onChange={handleSearchChange} placeholder={t('metricsList.search')} />
          </Col>
          <Col>
            <OverlayTrigger placement={"top"} overlay={<Tooltip id={`tooltip-alphaSort`}>Sort by alphabetical order</Tooltip>}>
              <span>
                <IconButton className={alphaSort > -1 ? "text-primary" : ""} onClick={() => activateSort('alpha')} >
                  <FontAwesomeIcon className="me-1" icon={alphaSort < 0 ? solid('sort') : alphaSort > 0 ? solid('sort-up') : solid('sort-down')} />
                  <div className='d-flex flex-column'>
                    <FontAwesomeIcon size='2xs' icon={solid('a')} />
                    <FontAwesomeIcon size='2xs' icon={solid('z')} />
                  </div>
                </IconButton>
              </span>
            </OverlayTrigger>
          </Col>
        </Row>

        {filteredMetrics.length === 0 && (
          <Alert className="mb-0 py-2">
            <FontAwesomeIcon icon={solid('info-circle')} />
            <span className="ms-2">{t('metricsList.noTasks')}</span>
          </Alert>
        )}

        <ListGroup>
          {filteredMetrics.map((metric, index) => (
            <ListGroup.Item key={index} className="d-flex align-items-center">
              <div className="pe-3">
                <FontAwesomeIcon icon={regular('check-circle')} size="lg" fixedWidth />
              </div>

              <Col className="overflow-hidden">
                <h6 className="mb-0 highlight" onClick={() => openDetailModal(metric)}>{metric.name}</h6>
                <p className="text-truncate mb-0">{metric.description}</p>
              </Col>

              <div className="ps-3 text-end">
                <IconButton className="ms-2" onClick={() => editMetric(metric)}>
                  <FontAwesomeIcon icon={solid('pen')} />
                </IconButton>
                <IconButton className="ms-2" onClick={() => openDeleteModal(metric)}>
                  <FontAwesomeIcon className="text-danger" icon={solid('trash')} />
                </IconButton>
              </div>
            </ListGroup.Item>
          ))}
        </ListGroup>
      </>
    )
  }

  const renderAsyncContent = () => {
    return metrics
      ? renderMetrics()
      : (
        <div className="d-flex justify-content-center py-5">
          <AppLoader size={150} />
        </div>
      )
  }

  return (
    <Container>
      <Card>
        <div className="d-flex align-items-center justify-content-between border-bottom py-2 px-3">
          <Card.Title className="mb-0">{t('metricsList.title')}</Card.Title>

          <Button variant="success" size="sm" onClick={() => openCreateModal()}>
            <FontAwesomeIcon icon={solid('plus')} />
            <span className="ms-2">{t('metricsList.create')}</span>
          </Button>
        </div>

        <Card.Body>{renderAsyncContent()}</Card.Body>
      </Card>

      <Modal show={showDeleteModal} onHide={handleDeleteModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>{t('common.deleteTitle', { item: t('common.metric') })}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('metricsList.deleteMessage')} <b>{metricToDelete?.name}</b>?

          <h5 className="my-4 text-center text-danger">{t('common.deleteWarning')}</h5>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="light" className="border" size="sm" onClick={handleDeleteModalClose}>{t('common.cancel')}</Button>
          <Button variant="danger" size="sm" onClick={deleteMetric}>{t('common.delete')}</Button>
        </Modal.Footer>
      </Modal>

      <Modal size="lg" show={showDetailModal} onHide={handleDetailModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>{`${metricDetail?.name}`}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row >
            <Col md={2}>
              <h6 className='mb-0'>Name </h6>
            </Col>
            <Col md={10}>
              <span>{metricDetail?.name}</span>
            </Col>
          </Row>
          <Row >
            <Col md={2}>
              <h6 className='mb-0'>Description </h6>
            </Col>
            <Col md={10}>
              <span>{metricDetail?.description}</span>
            </Col>
          </Row>
          <Row >
            <Col md={2}>
              <h6 className='mb-0'>Default Value </h6>
            </Col>
            <Col md={10}>
              <span>{metricDetail?.defaultValue}</span>
            </Col>
          </Row>
          <Row >
            <Col md={2}>
              <h6 className='mb-0'>Measure unit </h6>
            </Col>
            <Col md={10}>
              <span>{metricDetail?.measureUnitId}</span>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="success" className="border" size="sm" onClick={handleDetailModalClose}>{t('common.ok')}</Button>
        </Modal.Footer>

      </Modal>

      <Modal show={showCreateModal} onHide={handleCreateModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>{editing !== undefined ? t('common.edit') : t('common.create')} {t('common.metric')}</Modal.Title>
        </Modal.Header>

        <Form validated={validated} noValidate onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group controlId="metric-name" className="mb-3">
              <Form.Label>{t('common.name')}</Form.Label>
              <Form.Control placeholder={t('common.name')} size="sm" value={metric.name} onChange={handleFieldChange('name')} required />
              <Form.Control.Feedback type="invalid">{t('errors.requiredField', { field: t('common.name') })}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group controlId="metric-description" className="mb-3">
              <Form.Label>{t('common.description')}</Form.Label>
              <Form.Control as="textarea" rows={3} placeholder={t('common.description')} size="sm" value={metric.description} onChange={handleFieldChange('description')} />
            </Form.Group>

            <Form.Group controlId="metric-defaultValue" className="mb-3">
              <Form.Label>{t('common.defaultValue')}</Form.Label>
              <Form.Control min="0" placeholder={t('common.defaultValue')} size="sm" value={metric.defaultValue} onChange={handleFieldChange('defaultValue')} />
              <Form.Control.Feedback type="invalid">{t('errors.requiredField', { field: t('common.defaultValue') })}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group controlId="metric-suggested-duration" className="mb-3">
              <Form.Label>{t('metricsList.measureUnitId')}</Form.Label>
              <Form.Control min="0" placeholder={t('metricsList.measureUnitId')} size="sm" value={metric.measureUnitId} onChange={handleFieldChange('measureUnitId')} />
              <Form.Control.Feedback type="invalid">{t('errors.requiredField', { field: t('metricsList.measureUnitId') })}</Form.Control.Feedback>
            </Form.Group>

          </Modal.Body>

          <Modal.Footer>
            <Button variant="light" className="border" size="sm" onClick={handleCreateModalClose}>{t('common.cancel')}</Button>
            <Button variant="success" size="sm" type="submit">{t('common.confirm')}</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container >
  )
}